import { Suspense, type SyntheticEvent, forwardRef } from 'react';
import { isLivePracticeEnabled, isWritingPracticeEnabled } from '@lingoda/feature-flags';
import { useSelector } from '@lingoda/hooks';
import { livePracticeLinkSelector } from '@lingoda/modules';
import { TabValue } from '@lingoda/router';
import { Box, Hidden, Tabs, type TabsActions } from '@lingoda/ui';
import { styled } from '@lingoda/ui/theme';
import { CreditsStore } from '../../Credits/CreditsStore';
import { Notifications } from '../../Notifications';
import { TopNavigationItems } from '../../NavigationBars/components';
import { UserMenu } from '../../UserMenu';

interface Props {
    orientation?: 'horizontal' | 'vertical';
    onChange?: () => void;
}

export interface ViewProps extends Props {
    handleChange: (_e: SyntheticEvent, newValue: boolean | TabValue) => void;
    value: boolean | TabValue;
    hasAccessToClasses: boolean;
    isFFreeTrialOrWasInTrial: boolean;
}

const RightTabs = styled(Tabs)(({ theme }) => ({
    '& .MuiTabs-flexContainer': {
        marginLeft: theme.spacing(1),
        alignItems: 'center',
        gap: theme.spacing(2),
    },
}));

export const Layout = forwardRef<TabsActions, ViewProps>(
    ({ handleChange, value, hasAccessToClasses, isFFreeTrialOrWasInTrial, ...props }, ref) => {
        const isRightTabsValue = [TabValue.Store, TabValue.Settings].includes(value as TabValue);
        const livePracticeLink = useSelector(livePracticeLinkSelector);

        return (
            <>
                <Tabs
                    action={ref}
                    onChange={handleChange}
                    value={!isRightTabsValue && value}
                    TabIndicatorProps={{
                        sx: { display: value === TabValue.BookClasses ? 'none' : 'block' },
                    }}
                    {...props}
                >
                    {/* value prop must be passed here to provide proper MUI Tab behaviour */}
                    <TopNavigationItems.Today value={TabValue.Home} />
                    <TopNavigationItems.MyClasses value={TabValue.MyClasses} />
                    <TopNavigationItems.MyCourse value={TabValue.CourseOverview} />
                    <TopNavigationItems.Practice value={TabValue.Practice} />
                    {isLivePracticeEnabled() && livePracticeLink && (
                        <TopNavigationItems.LivePractice value={TabValue.LivePractice} />
                    )}
                    {isWritingPracticeEnabled() && (
                        <TopNavigationItems.Writing value={TabValue.Writing} />
                    )}
                    <Suspense fallback={null}>
                        <TopNavigationItems.BookClasses value={TabValue.BookClasses} />
                    </Suspense>
                </Tabs>
                <Hidden mdDown>
                    <Box flex={1} />
                    <RightTabs
                        action={ref}
                        onChange={handleChange}
                        value={isRightTabsValue && value}
                        {...props}
                    >
                        {(!hasAccessToClasses || isFFreeTrialOrWasInTrial) && (
                            <TopNavigationItems.Prices value={TabValue.Store} />
                        )}
                        {hasAccessToClasses && <CreditsStore />}
                        <Notifications />
                        <UserMenu value={TabValue.Settings} />
                    </RightTabs>
                </Hidden>
            </>
        );
    },
);

Layout.displayName = 'Layout';
